// next-app/src/hooks/use-check-out.ts
'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useCheckStore } from '@/store/check-store';
import { useAuthStore } from '@/store/auth-store';
import { queryKeys } from './query-keys';
import type { CheckOutDataResponse, CheckOutRecord } from '@/types';

// ============================================================================
// Check-out Data Query
// ============================================================================

interface UseCheckOutDataOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

/**
 * Hook to fetch check-out data for an event.
 * Returns only participants who have checked in but not checked out.
 *
 * Usage:
 * ```tsx
 * const { data, isLoading } = useCheckOutData(eventId);
 * ```
 */
export function useCheckOutData(eventId: number, options: UseCheckOutDataOptions = {}) {
  const token = useAuthStore((state) => state.dealerToken);
  const setCheckOutData = useCheckStore((state) => state.setCheckOutData);

  return useQuery({
    queryKey: queryKeys.check.outData(eventId),
    queryFn: async () => {
      const { response, result } = await api.getCheckOutData(eventId);

      if (!response.ok) {
        throw new Error('Failed to load check-out data');
      }

      if (result) {
        setCheckOutData(result.participants, result.vehicles);
      }

      return result as CheckOutDataResponse;
    },
    enabled: options.enabled !== false && Boolean(token) && Boolean(eventId),
    refetchInterval: options.refetchInterval ?? 30000, // Default: refresh every 30 seconds
    staleTime: 10000, // 10 seconds
  });
}

// ============================================================================
// Check-out Mutation
// ============================================================================

interface UseCheckOutOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to check out a participant.
 *
 * Usage:
 * ```tsx
 * const checkOut = useCheckOut(eventId);
 * checkOut.mutate({
 *   participant_id: 123,
 *   checked_out: new Date(),
 *   checked_out_by: 'dealer',
 *   test_drove: true,
 *   vehicles_driven: [1, 2, 3]
 * });
 * ```
 */
export function useCheckOut(eventId: number, options: UseCheckOutOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CheckOutRecord) => {
      const { response, result } = await api.checkOut(data);

      if (!response.ok) {
        throw new Error('Failed to check out participant');
      }

      return result;
    },
    onSuccess: () => {
      // Invalidate check-out data to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.check.outData(eventId) });

      // Also invalidate check-in data (participant status changed)
      queryClient.invalidateQueries({ queryKey: queryKeys.check.inData(eventId) });

      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: queryKeys.stats.event(eventId) });

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useCheckOut] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Selectors for filtered participants
// ============================================================================

/**
 * Hook to get filtered check-out participants based on search term.
 * Returns only participants who have checked in but not checked out.
 */
export function useFilteredCheckOutParticipants() {
  const participants = useCheckStore((state) => state.checkOutParticipants);
  const search = useCheckStore((state) => state.checkOutSearch);

  // Filter for checked-in but not checked-out participants
  const eligibleParticipants = participants.filter((p) => p.checked_in && !p.checked_out);

  if (!search) {
    return eligibleParticipants;
  }

  const searchLower = search.toLowerCase();

  return eligibleParticipants.filter(
    (p) =>
      p.first_name.toLowerCase().includes(searchLower) ||
      p.last_name.toLowerCase().includes(searchLower) ||
      p.email.toLowerCase().includes(searchLower) ||
      p.phone.includes(search)
  );
}
